import { useState } from "react";
import { toast } from "sonner";
import { FileSignature, User, Users, Receipt, Phone, ExternalLink, ShieldCheck, Loader2, Lock, Unlock } from "lucide-react";
import api from "@/lib/api";

const CARING_HEADER = {
  legal_name: "CARING POINT - APOIO DOMICILIÁRIO, UNIPESSOAL, LDA.",
  nif: "516 656 122",
};

function PartyReadOnly({ party, label, testid }) {
  if (!party) return null;
  const fields = [
    ["Nome", party.name],
    ["Morada", party.address],
    ["Cód. Postal / Localidade",
      [party.postal_code, party.locality].filter(Boolean).join(" · ")],
    ["Telefone", party.phone],
    ["E-mail", party.email],
    ["NIF", party.nif],
    ["BI / CC / Passaporte", party.id_document],
  ].filter(([, v]) => v);
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">{label}</div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        {fields.map(([k, v]) => (
          <div key={k} className="flex flex-col">
            <dt className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70">{k}</dt>
            <dd className="text-foreground">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function ContractDataView({ lead, proposalToken, currentUser, onChanged }) {
  const cd = lead?.contract_data;
  const acceptance = lead?.acceptance || {};
  const publicHref = proposalToken ? `/proposal/${proposalToken}/contract-data` : null;
  const isValidated = !!cd?.gestor_validated;
  const canValidate = !!(currentUser && (currentUser.role === "admin" || currentUser.role === "gestor"));
  const isAdmin = currentUser?.role === "admin";
  const [validating, setValidating] = useState(false);
  const [showNotesInput, setShowNotesInput] = useState(false);
  const [notes, setNotes] = useState("");

  const doValidate = async () => {
    setValidating(true);
    try {
      await api.post(`/leads/${lead.id}/contract-data/validate`, { notes });
      toast.success("Dados de contrato validados (IBAN/morada). Libertação do Plano de Cuidados fica sujeita à aprovação da Direção Técnica.");
      setShowNotesInput(false);
      setNotes("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao validar");
    } finally { setValidating(false); }
  };

  const doUnvalidate = async () => {
    if (!window.confirm("Reverter a validação dos dados de contrato? Esta ação não afecta a aprovação da Direção Técnica sobre o Plano de Cuidados.")) return;
    setValidating(true);
    try {
      await api.post(`/leads/${lead.id}/contract-data/unvalidate`);
      toast.success("Validação revertida");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setValidating(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="contract-data-block">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
            Dados de Contrato
          </div>
          <h3 className="font-ui text-lg inline-flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-foreground" />
            Preparação do contrato (pré-PandaDoc)
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {cd?.completed ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/40 text-[hsl(var(--success))]" data-testid="contract-data-status-badge">
              Submetidos
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border bg-muted border-border text-muted-foreground" data-testid="contract-data-status-badge">
              Aguarda cliente
            </span>
          )}
          {publicHref && (
            <a
              href={publicHref}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5"
              data-testid="contract-data-public-link"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Abrir formulário público
            </a>
          )}
        </div>
      </div>

      {!acceptance.accepted && (
        <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          O cliente ainda não aceitou a proposta. Assim que aceitar, o formulário para os dados de contrato fica desbloqueado no link público.
        </div>
      )}

      {acceptance.accepted && !cd?.completed && (
        <div className="rounded-lg border border-[hsl(var(--warning))]/25 bg-[hsl(var(--warning-bg))] p-4 text-sm text-[hsl(var(--warning))]">
          O cliente aceitou mas ainda não preencheu os dados de contrato. Envie-lhe o link ou aguarde submissão.
        </div>
      )}

      {/* Manager validation gate — releases the care plan link to the client */}
      {cd?.completed && (
        <div className={`mt-4 rounded-xl border p-4 ${isValidated ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" : "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/30"}`} data-testid="contract-validation-block">
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isValidated ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"}`}>
              {isValidated ? <ShieldCheck className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            </div>
            <div className="flex-1">
              <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-1 ${isValidated ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
                {isValidated ? "Contrato validado" : "Aguarda validação da Gestora"}
              </div>
              {isValidated ? (
                <div className="text-sm text-foreground leading-relaxed">
                  <b>{cd.gestor_validated_by_name || "Gestora"}</b> validou em {cd.gestor_validated_at ? new Date(cd.gestor_validated_at).toLocaleString("pt-PT") : "—"}. O Plano de Cuidados foi libertado para o cliente.
                  {cd.gestor_validation_notes && (
                    <blockquote className="mt-2 border-l-2 border-emerald-400 pl-3 italic text-foreground text-xs">
                      "{cd.gestor_validation_notes}"
                    </blockquote>
                  )}
                </div>
              ) : (
                <div className="text-sm text-foreground leading-relaxed">
                  Reveja os dados abaixo. Ao validar, o Plano de Cuidados fica automaticamente disponível para o cliente no link público e receberá email de notificação.
                </div>
              )}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {!isValidated && canValidate && !showNotesInput && (
                  <button onClick={() => setShowNotesInput(true)}
                    className="inline-flex items-center gap-1.5 text-xs bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-primary-foreground font-semibold rounded-full px-4 py-2"
                    data-testid="validate-contract-btn">
                    <ShieldCheck className="w-3.5 h-3.5" /> Validar dados de contrato (IBAN/morada)
                  </button>
                )}
                {!isValidated && canValidate && showNotesInput && (
                  <div className="w-full space-y-2">
                    <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Notas internas (opcional) — ex: NIF corrigido, dados validados telefonicamente…"
                      className="w-full bg-card border border-border rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-[hsl(var(--warning))] resize-y"
                      data-testid="validate-notes" />
                    <div className="flex items-center gap-2">
                      <button onClick={doValidate} disabled={validating}
                        className="inline-flex items-center gap-1.5 text-xs bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-primary-foreground font-semibold rounded-full px-4 py-2 disabled:opacity-50"
                        data-testid="validate-contract-confirm">
                        {validating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                        Confirmar validação
                      </button>
                      <button onClick={() => { setShowNotesInput(false); setNotes(""); }} disabled={validating}
                        className="text-xs text-muted-foreground hover:text-primary px-3 py-2"
                        data-testid="validate-contract-cancel">
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
                {isValidated && isAdmin && (
                  <button onClick={doUnvalidate} disabled={validating}
                    className="inline-flex items-center gap-1.5 text-xs bg-card border border-border hover:border-[hsl(var(--danger))]/40 text-muted-foreground hover:text-[hsl(var(--danger))] rounded-full px-3 py-1.5 disabled:opacity-50"
                    data-testid="unvalidate-contract-btn">
                    <Unlock className="w-3 h-3" /> Reverter validação
                  </button>
                )}
                {!canValidate && !isValidated && (
                  <div className="text-xs text-muted-foreground italic">Apenas o gestor atribuído pode validar.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {cd?.completed && (
        <div className="space-y-4" data-testid="contract-data-content">
          {/* 1º Outorgante Caring */}
          <div className="rounded-xl border border-primary/40 bg-muted/40 p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold mb-1">1.º Outorgante</div>
            <div className="font-ui text-sm text-foreground">{CARING_HEADER.legal_name} · NIF {CARING_HEADER.nif}</div>
          </div>

          {/* Beneficiaries */}
          {(cd.beneficiaries || []).map((b, i) => (
            <div key={i}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold mb-2 flex items-center gap-1.5">
                <User className="w-3 h-3" />
                2.º Outorgante — Beneficiário{(cd.beneficiaries.length > 1) ? ` #${i + 1}` : ""}
              </div>
              <PartyReadOnly party={b} label="Dados" testid={`contract-beneficiary-${i}`} />
            </div>
          ))}

          {/* Family */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold mb-2 flex items-center gap-1.5">
              <Users className="w-3 h-3" />
              2.º Outorgante — Responsável Familiar
            </div>
            <PartyReadOnly party={cd.family_responsible} label="Dados" testid="contract-family" />
          </div>

          {/* Secondary contact */}
          {(cd.secondary_contact?.name || cd.secondary_contact?.phone) && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold mb-2 flex items-center gap-1.5">
                <Phone className="w-3 h-3" /> Contacto secundário
              </div>
              <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm" data-testid="contract-secondary">
                <div><b>{cd.secondary_contact.name}</b> {cd.secondary_contact.relationship ? `· ${cd.secondary_contact.relationship}` : ""}</div>
                <div className="text-muted-foreground">{cd.secondary_contact.phone || "—"} · {cd.secondary_contact.email || "—"}</div>
              </div>
            </div>
          )}

          {/* Billing */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold mb-2 flex items-center gap-1.5">
              <Receipt className="w-3 h-3" /> Faturação
            </div>
            <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm" data-testid="contract-billing">
              {cd.billing?.target === "beneficiary" && (
                <div>Em nome do <b>Beneficiário{cd.beneficiaries?.length > 1 ? ` #${(cd.billing.beneficiary_index || 0) + 1}` : ""}</b> — {cd.beneficiaries?.[cd.billing.beneficiary_index || 0]?.name}</div>
              )}
              {cd.billing?.target === "family" && (
                <div>Em nome do <b>Responsável Familiar</b> — {cd.family_responsible?.name}</div>
              )}
              {cd.billing?.target === "other" && cd.billing.other_party && (
                <>
                  <div className="mb-2">Em nome de <b>terceiro</b>:</div>
                  <PartyReadOnly party={cd.billing.other_party} label="Faturado" testid="contract-billing-other" />
                </>
              )}
            </div>
          </div>

          {cd.submitted_at && (
            <div className="text-xs text-muted-foreground pt-2 border-t border-border">
              Submetido pelo cliente em {new Date(cd.submitted_at).toLocaleString("pt-PT")}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
